/**
 * The shot-budget model: pure functions, no DOM. The page, the copyable top
 * sheet and the tests all compute from here so they can never disagree.
 *
 *   keepers    = AI seconds on screen ÷ seconds used per keeper
 *   takes run  = keepers × takes per keeper
 *   generation = keepers × (drafts × len × draftRate + finals × len × finalRate) + stills + HDR (Topaz Hyperion rate)
 *   people     = (generations run ÷ generations per creator-day + pre-production days) × day rate
 *   cleanup    = keepers × share flagged × (hours ÷ 8) × compositor day rate
 *   range      = soft inputs (seconds used, artist pace) ÷ and × RANGE_FACTOR
 */

import type { ShotBudgetRates } from '@/lib/marketing-config'

export type Resolution = '720' | '1080' | '4k'

export interface BudgetParams {
  /** Runtime of the whole piece, minutes. */
  runtime: number
  /** Share of the runtime that is AI, percent. */
  ai: number
  res: Resolution
  /** Length of each take the model generates, seconds. */
  clipLen: number
  /** Seconds of a keeper that end up on screen. */
  shotLen: number
  /** Takes generated per keeper. */
  tries: number
  /** Share of takes run as 480p drafts, percent. */
  draftShare: number
  /** Look-dev stills per keeper. */
  imgs: number
  hdr: boolean
  /** Generations one creator gets through in a day: prompt, wait, review, pick. */
  gensPerDay: number
  artistRate: number
  team: number
  /** Creator-days before the first shot: look dev, style frames, characters, sets, tests. */
  prepDays: number
  /** Share of keepers that need a cleanup pass, percent. */
  cleanShare: number
  cleanHours: number
  cleanRate: number
}

export type NumericParam = Exclude<keyof BudgetParams, 'res' | 'hdr'>

/** Low / high scenario: the two soft inputs ÷ and × this. */
export const RANGE_FACTOR = 1.3
/**
 * Bumped when the formulas or defaults change. Shared links and the Excel
 * carry it (`v=`), so a saved budget can be recognised as older than the
 * current model; rates are deliberately not pinned (they are live).
 */
export const MODEL_VERSION = 1
/** Date the model or its defaults last changed (ISO date). Shown on the page as a freshness signal. */
export const MODEL_UPDATED = '2026-10-10'
const WORKING_DAYS_PER_MONTH = 21

/** Pre-production scales with the piece: half a creator-day per minute of runtime, rounded up. */
export const defaultPrepDays = (runtime: number): number => Math.ceil(runtime * 0.5)

/**
 * Longest generation each delivery route can produce: Seedance 2.5 (720p/1080p)
 * goes to 30 s, Seedance 2.0 (the 4K route) to 15 s. The model, the URL parser
 * and the slider all clamp to it so a 4K budget never prices a 30 s generation.
 */
export const MAX_CLIP_LEN: Record<Resolution, number> = { '720': 30, '1080': 30, '4k': 15 }
export const clipLenFor = (res: Resolution, clipLen: number): number => Math.min(clipLen, MAX_CLIP_LEN[res])

export const DEFAULTS: BudgetParams = {
  runtime: 10,
  ai: 100,
  res: '1080',
  clipLen: 15,
  shotLen: 4,
  tries: 25,
  draftShare: 80,
  imgs: 40,
  hdr: false,
  gensPerDay: 150,
  artistRate: 900,
  team: 3,
  prepDays: 5,
  cleanShare: 10,
  cleanHours: 4,
  cleanRate: 400,
}

/** Short query keys. Stable: they end up in people's decks and emails. */
export const URL_KEYS: Record<NumericParam, string> = {
  runtime: 'rt',
  ai: 'ai',
  clipLen: 'cl',
  shotLen: 'su',
  tries: 'tr',
  draftShare: 'dr',
  imgs: 'st',
  gensPerDay: 'gd',
  artistRate: 'ar',
  team: 'tm',
  prepDays: 'pp',
  cleanShare: 'cs',
  cleanHours: 'ch',
  cleanRate: 'cr',
}

/** Hard bounds for the typed number and the URL; the slider domain may be narrower. */
export const BOUNDS: Record<NumericParam, [number, number]> = {
  runtime: [0.5, 600],
  ai: [0, 100],
  clipLen: [4, 30],
  shotLen: [0.5, 30],
  tries: [1, 1000],
  draftShare: [0, 100],
  imgs: [0, 500],
  gensPerDay: [10, 2000],
  artistRate: [0, 10000],
  team: [1, 100],
  prepDays: [0, 365],
  cleanShare: [0, 100],
  cleanHours: [0.5, 80],
  cleanRate: [0, 10000],
}

// runtime stays fractional: a 30 s spot is 0.5 min, and BOUNDS allows it.
const INTEGER_PARAMS = new Set<NumericParam>(['tries', 'team', 'prepDays', 'imgs', 'gensPerDay'])

const RESOLUTIONS: readonly Resolution[] = ['720', '1080', '4k']
export const isResolution = (value: string): value is Resolution => (RESOLUTIONS as readonly string[]).includes(value)

export const clamp = (param: NumericParam, value: number): number => {
  const [lo, hi] = BOUNDS[param]
  const bounded = Math.min(hi, Math.max(lo, value))
  return INTEGER_PARAMS.has(param) ? Math.round(bounded) : bounded
}

/** Reads a query string into params. Unknown keys are ignored; values are clamped; never throws. */
export function parseParams(search: string): BudgetParams {
  const q = new URLSearchParams(search)
  const params: BudgetParams = { ...DEFAULTS }
  for (const [param, key] of Object.entries(URL_KEYS) as [NumericParam, string][]) {
    const raw = q.get(key)
    if (raw === null) continue
    const value = Number(raw)
    if (Number.isFinite(value)) params[param] = clamp(param, value)
  }
  if (q.get(URL_KEYS.prepDays) === null) params.prepDays = defaultPrepDays(params.runtime)
  const res = q.get('res')
  if (res && isResolution(res)) params.res = res
  if (q.get('hdr') === '1') params.hdr = true
  params.clipLen = clipLenFor(params.res, params.clipLen)
  params.shotLen = Math.min(params.shotLen, params.clipLen)
  return params
}

/** Every query key the calculator owns. Anything else on the URL (utm_*, fbclid…) is not ours and is left alone. */
export const BUDGET_URL_KEYS: ReadonlySet<string> = new Set([...Object.values(URL_KEYS), 'res', 'hdr', 'v'])

/** The model version a shared link was made with; null for a bare URL. */
export function linkVersion(search: string): number | null {
  const v = Number(new URLSearchParams(search).get('v'))
  return Number.isInteger(v) && v > 0 ? v : null
}

/** Writes only the non-default params, so a default budget is just `/production-calculator`. */
export function serializeParams(params: BudgetParams): string {
  const q = new URLSearchParams()
  for (const [param, key] of Object.entries(URL_KEYS) as [NumericParam, string][]) {
    const def = param === 'prepDays' ? defaultPrepDays(params.runtime) : DEFAULTS[param]
    if (params[param] !== def) q.set(key, String(params[param]))
  }
  if (params.res !== DEFAULTS.res) q.set('res', params.res)
  if (params.hdr) q.set('hdr', '1')
  // The address bar stays clean at the defaults; the share and Excel actions stamp `v` themselves (see shareUrl in FilmBudget).
  if (q.size > 0) q.set('v', String(MODEL_VERSION))
  return q.toString()
}

export interface BudgetResult {
  aiMin: number
  finSec: number
  used: number
  keepers: number
  takes: number
  drafts: number
  finals: number
  draftEach: number
  finalEach: number
  perKeeper: number
  allFinal: number
  stills: number
  hdr: number
  gen: number
  /** Finished shots one creator delivers in a day: generations per day ÷ generations per shot. */
  shotsPerDay: number
  /** Creator-days spent on shots, before pre-production is added. */
  shotDays: number
  artistDays: number
  art: number
  months: number
  flagged: number
  perCleanup: number
  cln: number
  total: number
}

export function compute(p: BudgetParams, rates: ShotBudgetRates, usedFactor = 1, paceFactor = 1): BudgetResult {
  const aiMin = (p.runtime * p.ai) / 100
  const clipLen = clipLenFor(p.res, p.clipLen)
  const finSec = aiMin * 60
  const used = Math.min(p.shotLen * usedFactor, clipLen)
  const keepers = finSec / Math.max(used, 0.1)
  const takes = keepers * p.tries
  // The kept take is always rendered at final, so at most tries-1 can be drafts and drafts + finals === tries.
  const drafts = Math.min(Math.round((p.tries * p.draftShare) / 100), Math.max(0, p.tries - 1))
  const finals = p.tries - drafts
  const draftEach = clipLen * rates.perSecondUsd.draft
  const finalEach = clipLen * rates.perSecondUsd[p.res]
  const perKeeper = drafts * draftEach + finals * finalEach
  const allFinal = p.tries * finalEach
  const stills = keepers * p.imgs * rates.stillUsd
  const hdr = p.hdr ? finSec * rates.hdrPerSecondUsd[p.res] : 0
  const gen = keepers * perKeeper + stills + hdr
  const shotsPerDay = (p.gensPerDay * paceFactor) / p.tries
  const shotDays = keepers / Math.max(shotsPerDay, 0.01)
  const artistDays = shotDays + p.prepDays
  const art = artistDays * p.artistRate
  const months = artistDays / p.team / WORKING_DAYS_PER_MONTH
  const flagged = (keepers * p.cleanShare) / 100
  const perCleanup = (p.cleanHours / 8) * p.cleanRate
  const cln = flagged * perCleanup
  return {
    aiMin,
    finSec,
    used,
    keepers,
    takes,
    drafts,
    finals,
    draftEach,
    finalEach,
    perKeeper,
    allFinal,
    stills,
    hdr,
    gen,
    shotsPerDay,
    shotDays,
    artistDays,
    art,
    months,
    flagged,
    perCleanup,
    cln,
    total: gen + art + cln,
  }
}

/** Low and high scenarios around the two inputs nobody can know in advance. */
export function range(p: BudgetParams, rates: ShotBudgetRates): { lo: number; hi: number } {
  const a = compute(p, rates, RANGE_FACTOR, RANGE_FACTOR).total
  const b = compute(p, rates, 1 / RANGE_FACTOR, 1 / RANGE_FACTOR).total
  return { lo: Math.min(a, b), hi: Math.max(a, b) }
}

// ── Log-scaled slider domains ──────────────────────────────────────────
// The slider position runs 0–100; the real value is the geometric interpolation.

export const LOG_DOMAINS: Partial<Record<NumericParam, [number, number]>> = {
  tries: [3, 90],
  artistRate: [100, 10000],
  cleanRate: [100, 2500],
}

export function logToValue(param: NumericParam, pos: number): number {
  const [lo, hi] = LOG_DOMAINS[param]!
  const raw = lo * Math.pow(hi / lo, pos / 100)
  return param === 'tries' ? Math.round(raw) : Math.round(raw / 50) * 50
}

export function valueToLog(param: NumericParam, value: number): number {
  const [lo, hi] = LOG_DOMAINS[param]!
  const clamped = Math.min(hi, Math.max(lo, value))
  return (Math.log(clamped / lo) / Math.log(hi / lo)) * 100
}

// ── Formatting ─────────────────────────────────────────────────────────

export const fmtUsd = (n: number): string => `$${Math.round(n).toLocaleString('en-US')}`
export const fmtUsdShort = (n: number): string =>
  n >= 1e6 ? `$${(n / 1e6).toFixed(2)}M` : n >= 1e3 ? `$${Math.round(n / 1e3)}K` : fmtUsd(n)
export const fmtCents = (n: number): string => `$${n.toFixed(2)}`
export const fmtInt = (n: number): string => Math.round(n).toLocaleString('en-US')
/** Minutes as "5 min", or seconds under a minute so a 30 s spot is not "0 min". */
export const fmtMin = (min: number): string => (min < 1 ? `${Math.round(min * 60)} s` : `${fmtInt(min)} min`)
/** Seconds as "20 s" or "1.5 min" once past a minute. */
export const fmtSec = (s: number): string =>
  s >= 60 ? `${(Math.round(s / 6) / 10).toLocaleString('en-US')} min` : `${Math.round(s * 10) / 10} s`
export const resLabel = (res: Resolution): string => (res === '4k' ? '4K' : `${res}p`)

/** The plain-text summary behind "Copy summary". */
export function topSheetText(p: BudgetParams, rates: ShotBudgetRates, pricingUrl: string, budgetUrl: string): string {
  const m = compute(p, rates)
  const { lo, hi } = range(p, rates)
  const lines = [
    'AI film budget',
    `${fmtMin(m.aiMin)} of AI in ${p.runtime} min · ${fmtInt(m.keepers)} shots · ${resLabel(p.res)}`,
    `Generation  ${fmtUsd(m.gen)}  (${fmtInt(m.takes)} generations, ${p.tries} per shot, ${p.clipLen} s each, ${p.draftShare}% drafts)`,
    `Personnel   ${fmtUsd(m.art)}  (${fmtInt(m.artistDays)} creator-days incl. ${p.prepDays} prep × ${fmtUsd(p.artistRate)}, ${p.team} creators ≈ ${m.months.toFixed(1)} months)`,
    `Cleanup     ${fmtUsd(m.cln)}  (${fmtInt(m.flagged)} × ${fmtUsd(m.perCleanup)})`,
    `Total       ${fmtUsd(m.total)}  · ${m.aiMin > 0 ? `${fmtUsdShort(m.total / m.aiMin)} per AI minute` : 'no AI minutes'} · ${fmtInt(m.gen / rates.oliveUsd)} olives · sensitivity range ${fmtUsdShort(lo)}–${fmtUsdShort(hi)}`,
    `Martini public rates (${pricingUrl}), Seedance 2.5. Estimates, not quotes. Picture only: voice, sound, music, editing and live action are a separate budget.`,
    budgetUrl,
  ]
  return lines.join('\n')
}
